"use client";

import { useQuery } from "@tanstack/react-query";
import agencyStatusService, { AgencyStatus } from "../services/agencyStatusService";
import AgencyTokenManager from "../services/agencyTokenManager";

export const AGENCY_STATUS_QUERY_KEY = ["agency-status", "me"];

export function useAgencyStatus() {
  // STALE-CONTEXT FIX (2026-07-23): the response depends on the CURRENT
  // agency (x-agency-id header), but the cache key didn't — after switching
  // agencies at login, the previous context's status (e.g. role: 'agent')
  // was served for up to 60s, firing the wrong gate ("Switch to Your Own
  // Agency" on the agency you own) until a hard refresh. Key per agency —
  // same pattern as useFeatureFlag. Existing invalidateQueries calls with
  // the base key still match (react-query prefix matching).
  const currentAgencyId = AgencyTokenManager.getCurrentAgencyId() || "none";
  const query = useQuery<AgencyStatus>({
    queryKey: [...AGENCY_STATUS_QUERY_KEY, currentAgencyId],
    queryFn: async () => {
      const result = await agencyStatusService.getMyStatus();
      return result;
    },
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: true, // Refetch when window gains focus to catch context changes
  });

  // CRITICAL FIX: DISABLE automatic agency sync to prevent infinite re-render loops
  // Agency sync should happen during login/authentication, not every time a component using
  // this hook mounts. Syncing here causes:
  // 1. AgencyTokenManager.setCurrentAgency() updates user context
  // 2. User context update triggers re-render of all components using useUserContext()
  // 3. Re-render calls useAgencyStatus() again
  // 4. Infinite loop causing React #310 error
  //
  // The agency sync is properly handled during the authentication flow.
  // This hook should ONLY fetch and return agency status data, not modify global state.

  return query;
}

export default useAgencyStatus;

